import { Card, Typography, Button, Input, Dropdown, Icon, Checkbox } from '@design-system/index';
import '../../DetailsPage.scss';
import { ColumnDef } from '@tanstack/react-table';
import { MemberSocialBenefitToCreate, MemberSocialBenefitToUpdate, SocialBenefit } from '@services/member/member/member.types';
import moment from 'moment';
import { formatMoney } from '@utils/numbers';
import { useDetailsSocialBenefitsVM } from './useDetailsSocialBenefitsVM';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { Formik } from 'formik';
import { ArchiveFilled, ArchiveLine, ModifierFull, ModifierLine, UnarchiveFilled, UnarchiveLine } from '@assets/images/icons';

export default function DetailsSocialBenefits() {
    const {
        id,
        socialBenefitColumnHelper,
        MySwal,
        updateMemberSocialBenefit,
        addMemberSocialBenefit,
        socialBenefits,
        needReload,
        setIsArchived,
        isArchived,
        handleMetadata,
        handleArchiveSocialBenefits,
        mutationHook,
        childrens,
        member
    } = useDetailsSocialBenefitsVM();

    const handleAddSocialBenefit = async () => {

        if(!member?.canHaveSocialBenefits) {
            await MySwal.fire({
                title: 'Vous ne pouvez pas ajouter une prestation sociale',
                text: 'Adhérent créé avant les 6 mois règlementaires. Vous devez attendre la date d\'activation des prestations sociales le ' + member?.canHaveSocialBenefitsAt,
                icon: 'warning',
                confirmButtonColor: '#01539D',
                iconColor: '#01539D',
            });
            return;
        }

        await MySwal.fire<MemberSocialBenefitToCreate>({
            title: "Création d'une prestation sociale",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <Formik
                    initialValues={{
                        template: '',
                        code: '',
                        label: '',
                        amount: 0,
                        observation: '',
                        checkNumber: '',
                        assignedAt: '',
                        selectedSocialBenefit: null,
                        isSocialBenefitForChild: false,
                        child: null
                    }}
                    onSubmit={(values) => {
                        console.log('values', values);
                        addMemberSocialBenefit({
                            id: String(id),
                            socialBenefit: {
                                code: values.code,
                                label: values.label,
                                amount: values.amount,
                                observation: values.observation,
                                checkNumber: values.checkNumber,
                                assignedAt: values.assignedAt,
                                child: values.child || null
                            }
                        });
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit, setFieldValue }) => (
                        <form id="form-add-member-social-benefit" onSubmit={handleSubmit}>
                            <Dropdown
                                required
                                name="template"
                                isSearchable
                                label="Modèle"
                                value={values.template}
                                onOptionChange={(option) => {
                                    const selected = socialBenefits.find((socialBenefit) => socialBenefit.label === option.label);
                                    setFieldValue('selectedSocialBenefit', selected);
                                    setFieldValue('template', option.label);
                                    setFieldValue('code', selected?.code || '');
                                    setFieldValue('label', selected?.label || '');
                                    setFieldValue('amount', selected?.amount || 0);
                                }}
                                options={socialBenefits.map((socialBenefit) => ({
                                    label: socialBenefit.label,
                                    value: socialBenefit.code,
                                    id: socialBenefit.id
                                }))}
                            />
                            {values.selectedSocialBenefit && (
                                <Checkbox
                                    checked={!!values.isSocialBenefitForChild}
                                    onChange={(v) => {
                                        setFieldValue('isSocialBenefitForChild', v);
                                        if (!v) {
                                            setFieldValue('child', undefined);
                                        }
                                    }}
                                >
                                    <Typography intrinsic="p" variant="p2" color="grey1">
                                        Prestation pour un enfant ?
                                    </Typography>
                                </Checkbox>
                            )}
                            {values.selectedSocialBenefit && values.isSocialBenefitForChild && (
                                <Dropdown
                                    name="child"
                                    label="Enfant"
                                    value={values.child}
                                    onOptionChange={(option) => {
                                        const selected = childrens.find((child) => child.id === option.id);
                                        setFieldValue('child', selected);
                                    }}
                                    options={childrens.map((child) => ({
                                        label: child.firstname + ' ' + child.lastname,
                                        value: child.id || 0,
                                        id: child.id || 0
                                    }))}
                                />
                            )}
                            {values.selectedSocialBenefit && (<>
                                <Input name="code" label="Code" placeholder="Code" required value={values.code} onChange={handleChange} />
                
                                <Input name="label" label="Libellé" placeholder="Libellé" required value={values.label} onChange={handleChange} />
                         
                                <Input
                                    type="number"
                                    rightContent={'€'}
                                    name="amount"
                                    label="Montant"
                                    placeholder="Montant"
                                    required
                                    value={values.amount}
                                    onChange={handleChange}
                                />
                          
                                <Input
                                    name="observation"
                                    label="Commentaire"
                                    placeholder="Commentaire"
                                    value={values.observation}
                                    onChange={handleChange}
                                />
                      
                                <Input
                                    name="checkNumber"
                                    label="Numéro de chèque"
                                    placeholder="Numéro de chèque"
                                    value={values.checkNumber}
                                    onChange={handleChange}
                                />
                          
                                <Input
                                    type="date"
                                    name="assignedAt"
                                    required
                                    label="Date d'attribution"
                                    placeholder="Date d'attribution"
                                    value={values.assignedAt}
                                    onChange={handleChange}
                                />
                            </>)}
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-add-member-social-benefit') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const handleModificationSocialBenefits = async (socialBenefit: MemberSocialBenefitToUpdate) => {
        await MySwal.fire<MemberSocialBenefitToCreate>({
            title: "Modification d'une prestation sociale perçue",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Modifier',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <Formik
                    initialValues={{
                        template: socialBenefit ? socialBenefits.find((sb) => sb.code === socialBenefit.code)?.label || '' : '',
                        code: socialBenefit?.code || '',
                        label: socialBenefit?.label || '',
                        amount: socialBenefit?.amount || 0,
                        observation: socialBenefit?.observation || '',
                        checkNumber: socialBenefit?.checkNumber || '',
                        assignedAt: socialBenefit?.assignedAt || new Date().toISOString().split('T')[0],
                        selectedSocialBenefit: socialBenefit ? socialBenefits.find((sb) => sb.code === socialBenefit.code) || null : null,
                        isSocialBenefitForChild: socialBenefit?.child ? true : false,
                        child: socialBenefit?.child || null
                    }}
                    onSubmit={(values) => {
                        updateMemberSocialBenefit({
                            id: String(id),
                            socialBenefit: {
                                id: socialBenefit?.id || 0,
                                code: values.code,
                                label: values.label,
                                amount: values.amount,
                                observation: values.observation,
                                checkNumber: values.checkNumber,
                                assignedAt: values.assignedAt,
                                child: values.child || null
                            }
                        });
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit, setFieldValue }) => (
                        <form id="form-edit-member-social-benefit" onSubmit={handleSubmit}>
                            <Input name="code" label="Code" placeholder="Code" value={values.code} required onChange={handleChange} />
                            <Input name="label" label="Libellé" placeholder="Libellé" value={values.label} required onChange={handleChange} />
                            <Input
                                type="number"
                                required
                                name="amount"
                                label="Montant"
                                placeholder="Montant"
                                rightContent={'€'}
                                value={values.amount}
                                onChange={handleChange}
                            />
                                <Checkbox
                                    checked={!!values.isSocialBenefitForChild}
                                    onChange={(v) => {
                                        setFieldValue('isSocialBenefitForChild', v);
                                        if (!v) {
                                            setFieldValue('child', undefined);
                                        }
                                    }}
                                >
                                    <Typography intrinsic="p" variant="p2" color="grey1">
                                        Prestation pour un enfant ?
                                    </Typography>
                                </Checkbox>
                            {values.isSocialBenefitForChild && (
                                <Dropdown
                                    name="child"
                                    label="Enfant"
                                    value={values.child?.id || 0}
                                    onOptionChange={(option) => {
                                        const selected = childrens.find((child) => child.id === option.id);
                                        setFieldValue('child', selected);
                                    }}
                                    options={childrens.map((child) => ({
                                        label: child.firstname + ' ' + child.lastname,
                                        value: child.id || 0,
                                        id: child.id || 0
                                    }))}
                                />
                            )}
                            <Input
                                name="observation"
                                label="Commentaire"
                                placeholder="Commentaire"
                                value={values.observation}
                                onChange={handleChange}
                            />
                            <Input
                                name="checkNumber"
                                label="Numéro de chèque"
                                placeholder="Numéro de chèque"
                                value={values.checkNumber}
                                onChange={handleChange}
                            />
                            <Input
                                type="date"
                                name="assignedAt"
                                required
                                label="Date d'attribution"
                                placeholder="Date d'attribution"
                                value={values.assignedAt}
                                onChange={handleChange}
                            />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-edit-member-social-benefit') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const socialBenefitsColumns = [
        socialBenefitColumnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        socialBenefitColumnHelper.accessor('assignedAt', {
            header: 'Attribué le',
            enableSorting: true,
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')}</>
        }),
        socialBenefitColumnHelper.accessor('recipientFullname', {
            id: 'child',
            header: 'Bénéficiaire',
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        socialBenefitColumnHelper.accessor('amount', {
            header: 'Montant',
            enableSorting: true,
            cell: (info) => <>{formatMoney(info.getValue())} </>
        }),
        socialBenefitColumnHelper.accessor('checkNumber', {
            header: 'Numéro de chèque',
            enableSorting: true,
            cell: (info) => <>{info.getValue()} </>
        }),
        socialBenefitColumnHelper.accessor('observation', {
            header: 'Commentaire',
            enableSorting: false,
            cell: (info) => <>{info.getValue()}</>
        }),
        socialBenefitColumnHelper.display({
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationSocialBenefits({
                                    code: info.row.original.code,
                                    label: info.row.original.label,
                                    amount: info.row.original.amount,
                                    observation: info.row.original.observation,
                                    checkNumber: info.row.original.checkNumber,
                                    assignedAt: info.row.original.assignedAt,
                                    id: info.row.original.id,
                                    child: info.row.original.child || null
                                });
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveSocialBenefits([info.row.original]);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<SocialBenefit>[];

    return (
        <>
            <div className="details-member-container">
                <div className="details-member-container-body">
                    <Card headerType="very-light" headerTitle="Prestations sociales perçues">
                        <MutationTable
                            noHeader
                            key={isArchived ? 'archived' : 'active'}
                            columns={socialBenefitsColumns}
                            mutationHook={mutationHook}
                            mutationHookParams={{ id: id }}
                            onRowClick={(row) =>
                                handleModificationSocialBenefits({
                                    code: row.code,
                                    label: row.label,
                                    amount: row.amount,
                                    observation: row.observation,
                                    checkNumber: row.checkNumber,
                                    assignedAt: row.assignedAt,
                                    id: row.id,
                                    child: row.child || null
                                })
                            }
                            defaultSorting={{
                                field: 'assignedAt',
                                direction: 'desc'
                            }}
                            deleting={{
                                enabled: isArchived,
                                entity: 'member_social_benefit'
                            }}
                            className="no-card"
                            needReload={needReload}
                            createButtonOnClick={() => handleAddSocialBenefit()}
                            createButtonLabel="Ajouter une prestation sociale"
                            archiveButtonOnClick={() => setIsArchived(!isArchived)}
                            archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                            onMetadata={handleMetadata}
                        />
                    </Card>
                </div>
            </div>
        </>
    );
}
